/**
 * Authentication Manager
 * Handles user authentication, session management, first-run setup,
 * password changes, and access control.
 *
 * OOA/OOD:
 * - Single Responsibility: authentication lifecycle
 * - Encapsulation: session state and storage are private
 * - Observer Pattern: dispatches auth events for UI coordination
 * - Strategy Pattern: storage strategy (sessionStorage vs localStorage)
 *
 * @module AuthManager
 */


// ============================================================================
// Type Definitions
// ============================================================================

interface SessionData {
    token: string;
    user: UserProfile;
}

interface UserProfile {
    id?: string;
    user_id?: string;
    username: string;
    email?: string;
    role: { [key: string]: null };
    createdAt?: bigint;
    created_at?: bigint;
    lastLogin?: bigint;
    last_login?: bigint;
    isActive?: boolean;
    is_active?: boolean;
}

interface LoginRequest {
    username: string;
    password: string;
}

interface LoginResult {
    success: boolean;
    user?: UserProfile;
    error?: string;
}

interface PasswordChangeResult {
    success: boolean;
    error?: string;
}

interface SessionToken {
    token: string;
}

interface CandidResult<T> {
    ok?: T;
    Ok?: T;
    err?: string;
    Err?: string;
}

type AuthEventType = 'login' | 'logout' | 'sessionValidated';

/**
 * Password strength levels
 */
const enum PasswordStrength {
    Weak = 1,
    Fair = 2,
    Good = 3,
    Strong = 4,
}

// ============================================================================
// PasswordValidator — Encapsulates password complexity rules
// ============================================================================

class PasswordValidator {
    private static readonly MIN_LENGTH = 8;

    /** Evaluate password strength (1-4) */
    static evaluate(password: string): PasswordStrength {
        let score = 0;
        if (password.length >= this.MIN_LENGTH) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/[a-z]/.test(password)) score++;
        if (/[0-9]/.test(password)) score++;
        return Math.max(1, score) as PasswordStrength;
    }

    /** Get color class for strength bar */
    static getColor(strength: PasswordStrength): string {
        switch (strength) {
            case PasswordStrength.Weak: return 'bg-red-500';
            case PasswordStrength.Fair: return 'bg-orange-500';
            case PasswordStrength.Good: return 'bg-yellow-500';
            case PasswordStrength.Strong: return 'bg-green-500';
        }
    }

    /** Get width percentage for strength bar */
    static getWidth(strength: PasswordStrength): string {
        return `${strength * 25}%`;
    }

    /** Check if password meets minimum requirements */
    static isValid(password: string): boolean {
        return password.length >= this.MIN_LENGTH;
    }
}

// ============================================================================
// AuthManager Class
// ============================================================================

export class AuthManager {
    private canisterClient: CanisterClient | null = null;
    private currentUser: UserProfile | null = null;
    private sessionToken: string | null = null;
    private readonly storageKey: string = 'asd_auth_session';
    private readonly rememberMeKey: string = 'asd_remember_me';

    constructor() {
        this.initializeSession();
    }

    // ========================================================================
    // Initialization
    // ========================================================================

    async initialize(canisterClient: CanisterClient): Promise<void> {
        this.canisterClient = canisterClient;

        if (this.sessionToken) {
            await this.validateCurrentSession();
        }
    }

    private initializeSession(): void {
        const rememberMe = localStorage.getItem(this.rememberMeKey) === 'true';
        const storage = rememberMe ? localStorage : sessionStorage;

        const sessionData = storage.getItem(this.storageKey);
        if (sessionData) {
            try {
                const { token, user } = JSON.parse(sessionData) as SessionData;
                this.sessionToken = token;
                this.currentUser = user;
            } catch (error) {
                console.error('Error parsing session data:', error);
                this.clearSession();
            }
        }
    }

    // ========================================================================
    // First-Run Detection — Setup wizard flow
    // ========================================================================

    /** Check if this is a fresh deployment with no users */
    async checkFirstRun(): Promise<boolean> {
        if (!this.canisterClient) return false;

        try {
            const result = await this.canisterClient.callCanister(
                'user_manager', 'isFirstRun', []
            );
            // Proxy returns "true"/"false" as string; handle both formats
            return result === true || result === 'true';
        } catch (error) {
            console.error('First-run check failed:', error);
            return false;
        }
    }

    /** Create initial admin account via setup wizard */
    async setupAdmin(username: string, email: string, password: string): Promise<LoginResult> {
        if (!this.canisterClient) {
            return { success: false, error: 'Not initialized' };
        }

        try {
            const result = await this.canisterClient.callCanister(
                'user_manager', 'setupInitialAdmin', [{
                    username,
                    email,
                    password,
                    role: { admin: null },
                }]
            ) as CandidResult<UserProfile>;

            const profile = result?.ok || result?.Ok;
            const error = result?.err || result?.Err;

            if (profile) {
                return { success: true, user: profile };
            }
            return { success: false, error: error || 'Setup failed' };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Setup failed',
            };
        }
    }

    // ========================================================================
    // Authentication — Login / Logout / Validate
    // ========================================================================

    async login(username: string, password: string, rememberMe: boolean = false): Promise<LoginResult> {
        try {
            if (!this.canisterClient) {
                throw new Error('AuthManager not initialized with canister client');
            }

            const loginRequest: LoginRequest = { username, password };
            const result = await this.canisterClient.callCanister(
                'user_manager', 'login', [loginRequest]
            ) as CandidResult<SessionToken>;

            const session = result?.ok || result?.Ok;
            const err = result?.err || result?.Err;

            if (!session) {
                throw new Error(err || 'Login failed');
            }

            this.sessionToken = session.token;

            const profileResult = await this.canisterClient.callCanister(
                'user_manager', 'getUserProfile', [session.token]
            ) as CandidResult<UserProfile>;

            const profile = profileResult?.ok || profileResult?.Ok;
            const profileErr = profileResult?.err || profileResult?.Err;

            if (!profile) {
                throw new Error(profileErr || 'Failed to get user profile');
            }

            this.currentUser = profile;
            this.storeSession(rememberMe);
            this.dispatchAuthEvent('login', this.currentUser);

            return { success: true, user: this.currentUser };
        } catch (error) {
            console.error('Login error:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    async logout(): Promise<void> {
        try {
            if (this.sessionToken && this.canisterClient) {
                await this.canisterClient.callCanister('user_manager', 'logout', [this.sessionToken]);
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            this.clearSession();
            this.dispatchAuthEvent('logout');
        }
    }

    async validateCurrentSession(): Promise<boolean> {
        if (!this.sessionToken || !this.canisterClient) {
            this.clearSession();
            return false;
        }

        try {
            const result = await this.canisterClient.callCanister(
                'user_manager', 'validateSession', [this.sessionToken]
            ) as CandidResult<UserProfile>;

            const userProfile = result?.ok || result?.Ok;
            const err = result?.err || result?.Err;

            if (userProfile) {
                this.currentUser = userProfile;
                this.dispatchAuthEvent('sessionValidated', this.currentUser);
                return true;
            }

            console.warn('Session validation failed:', err);
            this.clearSession();
            return false;
        } catch (error) {
            console.error('Session validation error:', error);
            this.clearSession();
            return false;
        }
    }

    // ========================================================================
    // Password Management
    // ========================================================================

    async changePassword(currentPassword: string, newPassword: string): Promise<PasswordChangeResult> {
        try {
            if (!this.sessionToken || !this.canisterClient) {
                throw new Error('Not authenticated');
            }

            const result = await this.canisterClient.callCanister(
                'user_manager', 'updatePassword', [
                    this.sessionToken, currentPassword, newPassword,
                ]
            ) as CandidResult<boolean>;

            const success = result?.ok || result?.Ok;
            const error = result?.err || result?.Err;

            if (success) {
                return { success: true };
            }
            throw new Error(error || 'Password change failed');
        } catch (error) {
            console.error('Password change error:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    // ========================================================================
    // Session Accessors
    // ========================================================================

    getCurrentUser(): UserProfile | null {
        return this.currentUser;
    }

    isAuthenticated(): boolean {
        return this.sessionToken !== null && this.currentUser !== null;
    }

    hasRole(role: string): boolean {
        if (!this.currentUser) return false;
        return Object.keys(this.currentUser.role)[0] === role;
    }

    isAdmin(): boolean {
        return this.hasRole('admin');
    }

    isOperator(): boolean {
        return this.hasRole('operator') || this.isAdmin();
    }

    getSessionToken(): string | null {
        return this.sessionToken;
    }

    // ========================================================================
    // Session Storage — Strategy: localStorage (remember) vs sessionStorage
    // ========================================================================

    private serializeBigInt(obj: any): any {
        if (obj === null || obj === undefined) return obj;
        if (typeof obj === 'bigint') return obj.toString();
        if (Array.isArray(obj)) return obj.map(item => this.serializeBigInt(item));
        if (typeof obj === 'object') {
            const result: any = {};
            for (const [key, value] of Object.entries(obj)) {
                result[key] = this.serializeBigInt(value);
            }
            return result;
        }
        return obj;
    }

    private storeSession(rememberMe: boolean = false): void {
        const sessionData: SessionData = {
            token: this.sessionToken!,
            user: this.serializeBigInt(this.currentUser!),
        };

        localStorage.setItem(this.rememberMeKey, rememberMe.toString());

        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem(this.storageKey, JSON.stringify(sessionData));

        const otherStorage = rememberMe ? sessionStorage : localStorage;
        otherStorage.removeItem(this.storageKey);
    }

    private clearSession(): void {
        this.sessionToken = null;
        this.currentUser = null;
        localStorage.removeItem(this.storageKey);
        sessionStorage.removeItem(this.storageKey);
        localStorage.removeItem(this.rememberMeKey);
    }

    // ========================================================================
    // Event Dispatch — Observer pattern for auth state changes
    // ========================================================================

    private dispatchAuthEvent(type: AuthEventType, data: UserProfile | null = null): void {
        document.dispatchEvent(new CustomEvent(`auth:${type}`, { detail: data }));
    }

    // ========================================================================
    // Login Modal UI — Show/hide/error for login dialog
    // ========================================================================

    requireAuth(): boolean {
        if (!this.isAuthenticated()) {
            this.showLoginModal();
            return false;
        }
        return true;
    }

    showLoginModal(): void {
        const loginModal = document.getElementById('loginModal');
        if (loginModal) {
            loginModal.style.display = 'flex';
            this.clearLoginError();
        }
    }

    hideLoginModal(): void {
        const loginModal = document.getElementById('loginModal');
        if (loginModal) {
            loginModal.style.display = 'none';
            this.clearLoginForm();
        }
    }

    showLoginError(message: string): void {
        const errorElement = document.getElementById('loginError');
        if (errorElement) {
            errorElement.textContent = message;
            errorElement.style.display = 'block';
        }
    }

    clearLoginError(): void {
        const errorElement = document.getElementById('loginError');
        if (errorElement) {
            errorElement.style.display = 'none';
            errorElement.textContent = '';
        }
    }

    clearLoginForm(): void {
        const form = document.getElementById('loginForm') as HTMLFormElement | null;
        if (form) form.reset();
        this.clearLoginError();
    }

    // ========================================================================
    // Setup Wizard UI — First-run admin account creation
    // ========================================================================

    showSetupWizard(): void {
        const wizard = document.getElementById('setupWizardModal');
        if (wizard) wizard.style.display = 'flex';
    }

    hideSetupWizard(): void {
        const wizard = document.getElementById('setupWizardModal');
        if (wizard) wizard.style.display = 'none';
    }

    initializeSetupWizard(): void {
        const form = document.getElementById('setupWizardForm') as HTMLFormElement | null;
        if (!form) return;

        // Password strength indicator
        const passwordInput = document.getElementById('setup-password') as HTMLInputElement | null;
        passwordInput?.addEventListener('input', () => {
            this.updatePasswordStrength(
                passwordInput.value,
                'setup-password-strength-bar',
                'setup-password-hint'
            );
        });

        // Form submission
        form.addEventListener('submit', async (e) => {
            e.preventDefault();

            const username = (document.getElementById('setup-username') as HTMLInputElement).value.trim();
            const email = (document.getElementById('setup-email') as HTMLInputElement).value.trim();
            const password = (document.getElementById('setup-password') as HTMLInputElement).value;
            const confirmPassword = (document.getElementById('setup-confirm-password') as HTMLInputElement).value;
            const errorEl = document.getElementById('setupError');
            const submitBtn = document.getElementById('setupSubmitBtn') as HTMLButtonElement;

            // Validation
            if (password !== confirmPassword) {
                this.showSetupError('Passwords do not match');
                return;
            }
            if (!PasswordValidator.isValid(password)) {
                this.showSetupError('Password must be at least 8 characters');
                return;
            }

            submitBtn.disabled = true;
            submitBtn.textContent = 'Creating account...';

            const result = await this.setupAdmin(username, email, password);

            if (result.success) {
                this.hideSetupWizard();
                // Auto-login with the new credentials
                const loginResult = await this.login(username, password, false);
                if (loginResult.success) {
                    window.location.reload();
                } else {
                    // Setup succeeded but auto-login failed — show login modal
                    this.showLoginModal();
                }
            } else {
                this.showSetupError(result.error || 'Failed to create admin account');
                submitBtn.disabled = false;
                submitBtn.textContent = 'Create Admin Account';
            }
        });
    }

    private showSetupError(message: string): void {
        const el = document.getElementById('setupError');
        if (el) {
            el.textContent = message;
            el.style.display = 'block';
        }
    }

    // ========================================================================
    // Change Password Modal UI
    // ========================================================================

    showChangePasswordModal(): void {
        const modal = document.getElementById('changePasswordModal');
        const form = document.getElementById('changePasswordForm') as HTMLFormElement | null;
        const errorEl = document.getElementById('changePasswordError');
        const successEl = document.getElementById('changePasswordSuccess');
        form?.reset();
        if (errorEl) errorEl.style.display = 'none';
        if (successEl) successEl.style.display = 'none';
        if (modal) modal.style.display = 'flex';
    }

    hideChangePasswordModal(): void {
        const modal = document.getElementById('changePasswordModal');
        if (modal) modal.style.display = 'none';
    }

    initializeChangePasswordModal(): void {
        // Password strength indicator
        const newPasswordInput = document.getElementById('cp-new-password') as HTMLInputElement | null;
        newPasswordInput?.addEventListener('input', () => {
            this.updatePasswordStrength(newPasswordInput.value, 'cp-password-strength-bar');
        });

        // Form submission
        const form = document.getElementById('changePasswordForm') as HTMLFormElement | null;
        form?.addEventListener('submit', async (e) => {
            e.preventDefault();

            const currentPassword = (document.getElementById('cp-current-password') as HTMLInputElement).value;
            const newPassword = (document.getElementById('cp-new-password') as HTMLInputElement).value;
            const confirmPassword = (document.getElementById('cp-confirm-password') as HTMLInputElement).value;
            const errorEl = document.getElementById('changePasswordError');
            const successEl = document.getElementById('changePasswordSuccess');
            const submitBtn = document.getElementById('changePasswordSubmitBtn') as HTMLButtonElement;

            if (errorEl) errorEl.style.display = 'none';
            if (successEl) successEl.style.display = 'none';

            if (newPassword !== confirmPassword) {
                if (errorEl) { errorEl.textContent = 'Passwords do not match'; errorEl.style.display = 'block'; }
                return;
            }
            if (!PasswordValidator.isValid(newPassword)) {
                if (errorEl) { errorEl.textContent = 'New password must be at least 8 characters'; errorEl.style.display = 'block'; }
                return;
            }

            submitBtn.disabled = true;
            submitBtn.textContent = 'Updating...';

            const result = await this.changePassword(currentPassword, newPassword);

            if (result.success) {
                if (successEl) { successEl.textContent = 'Password updated successfully'; successEl.style.display = 'block'; }
                setTimeout(() => this.hideChangePasswordModal(), 1500);
            } else {
                if (errorEl) { errorEl.textContent = result.error || 'Failed to change password'; errorEl.style.display = 'block'; }
            }

            submitBtn.disabled = false;
            submitBtn.textContent = 'Update Password';
        });

        // Close button
        document.querySelectorAll('.change-password-close').forEach(btn => {
            btn.addEventListener('click', () => this.hideChangePasswordModal());
        });
    }

    // ========================================================================
    // User Menu Dropdown — Header dropdown with Change Password + Logout
    // ========================================================================

    initializeUserMenu(): void {
        const btn = document.getElementById('userMenuBtn');
        const dropdown = document.getElementById('userMenuDropdown');
        const changePasswordBtn = document.getElementById('changePasswordBtn');

        if (btn && dropdown) {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                dropdown.classList.toggle('hidden');
            });

            // Close on click outside
            document.addEventListener('click', () => {
                dropdown.classList.add('hidden');
            });

            dropdown.addEventListener('click', (e) => {
                e.stopPropagation();
            });
        }

        changePasswordBtn?.addEventListener('click', () => {
            dropdown?.classList.add('hidden');
            this.showChangePasswordModal();
        });

        const logoutBtn = dropdown?.querySelector('.logout-btn');
        logoutBtn?.addEventListener('click', async (e) => {
            e.preventDefault();
            await this.logout();
            window.location.reload();
        });
    }

    // ========================================================================
    // Password Strength UI — Shared strength bar updater
    // ========================================================================

    private updatePasswordStrength(password: string, barId: string, hintId?: string): void {
        const bar = document.getElementById(barId);
        if (!bar) return;

        if (password.length === 0) {
            bar.style.width = '0%';
            bar.className = 'h-1 rounded-full transition-all duration-300';
            return;
        }

        const strength = PasswordValidator.evaluate(password);
        bar.style.width = PasswordValidator.getWidth(strength);
        bar.className = `h-1 rounded-full transition-all duration-300 ${PasswordValidator.getColor(strength)}`;

        if (hintId) {
            const hint = document.getElementById(hintId);
            if (hint) {
                const labels = ['Weak', 'Fair', 'Good', 'Strong'];
                hint.textContent = labels[strength - 1];
                hint.className = `text-xs mt-1 ${strength >= 3 ? 'text-green-600' : 'text-gray-400'}`;
            }
        }
    }

    // ========================================================================
    // Login Form Initialization — Handles form submit, close handlers
    // ========================================================================

    initializeLoginForm(): void {
        const loginForm = document.getElementById('loginForm') as HTMLFormElement | null;
        if (loginForm) {
            loginForm.addEventListener('submit', async (e) => {
                e.preventDefault();

                const username = (document.getElementById('username') as HTMLInputElement).value.trim();
                const password = (document.getElementById('password') as HTMLInputElement).value;
                const rememberMe = (document.getElementById('rememberMe') as HTMLInputElement).checked;

                if (!username || !password) {
                    this.showLoginError('Please enter both username and password');
                    return;
                }

                const submitBtn = loginForm.querySelector('button[type="submit"]') as HTMLButtonElement;
                const originalText = submitBtn.textContent || '';
                submitBtn.textContent = 'Logging in...';
                submitBtn.disabled = true;

                try {
                    const result = await this.login(username, password, rememberMe);
                    if (result.success) {
                        this.hideLoginModal();
                        window.location.reload();
                    } else {
                        this.showLoginError(result.error || 'Login failed');
                    }
                } catch (error) {
                    this.showLoginError('An unexpected error occurred');
                } finally {
                    submitBtn.textContent = originalText;
                    submitBtn.disabled = false;
                }
            });
        }

        // Close modal handlers
        document.querySelectorAll('.close-modal').forEach(button => {
            button.addEventListener('click', () => this.hideLoginModal());
        });

        const loginModal = document.getElementById('loginModal');
        if (loginModal) {
            loginModal.addEventListener('click', (e) => {
                if (e.target === loginModal) this.hideLoginModal();
            });
        }
    }

    initializeLogout(): void {
        document.addEventListener('click', async (e) => {
            const target = e.target as HTMLElement;
            if (target.matches('.logout-btn, .logout-btn *')) {
                e.preventDefault();
                await this.logout();
                window.location.reload();
            }
        });
    }

    // ========================================================================
    // UI Updates — Sync DOM with auth state
    // ========================================================================

    updateUI(): void {
        const isAuth = this.isAuthenticated();
        const user = this.getCurrentUser();

        document.querySelectorAll('.auth-required').forEach(el => {
            (el as HTMLElement).style.display = isAuth ? '' : 'none';
        });

        document.querySelectorAll('.auth-not-required').forEach(el => {
            (el as HTMLElement).style.display = isAuth ? 'none' : '';
        });

        if (isAuth && user) {
            document.querySelectorAll('.user-username').forEach(el => {
                el.textContent = user.username;
            });

            document.querySelectorAll('.user-role').forEach(el => {
                const role = Object.keys(user.role)[0];
                el.textContent = role.charAt(0).toUpperCase() + role.slice(1);
            });

            document.querySelectorAll('.admin-only').forEach(el => {
                (el as HTMLElement).style.display = this.isAdmin() ? '' : 'none';
            });

            document.querySelectorAll('.operator-only').forEach(el => {
                (el as HTMLElement).style.display = this.isOperator() ? '' : 'none';
            });
        }
    }
}

// Create global instance
declare global {
    interface Window {
        authManager: AuthManager;
    }
}

window.authManager = new AuthManager();
